import { useRef, useState } from 'react'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { H2 } from '@/common/ui'
import { useNavigate } from 'react-router'
import { APP_ROUTES } from '@/common/constants'
import AccountTypeSelector from '@/user/account-type/AccountTypeSelector'
import { Button } from '@/common/ui/button'
import { ACCOUNT_TYPE_LABELS, ONBOARDING_COPY } from '@/locales/en'
import { APPLICATION_COPY } from '@/locales/en'
import { ApplicationProgressBar } from './ProgressBar'
import { KycRequirements } from '@/kyc/KycRequirements'
import { AccountType } from '@/common/types'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import AddAccountModal from './addAccountModal'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/common/ui/dialog'
import { Logo } from '@/common/assets/svg'
import { resolveHomeRouteForContext } from '@/common/navigation/accountRoutes'

export const ApplicationCreatePage = () => {
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const primaryAccount = usePrimaryAccount()
    const isAdviserCreateFlow = primaryAccount?.accountType === AccountType.ADVISER
    const navigate = useNavigate()
    const nextButtonRef = useRef<HTMLButtonElement>(null)

    const [selectedAccountType, setSelectedAccountType] = useState<AccountType | undefined>(account?.accountType)

    if (!user) {
        return <FullScreenLoader />
    }

    const goToKycConfirm = () => {
        if (!selectedAccountType) return
        if (!activeAccountId) return

        navigate(APP_ROUTES.APPLICATION_KYC_CONFIRM, {
            state: {
                accountType: selectedAccountType,
                createdAccountId: activeAccountId,
                isAdviser: false,
            },
        })
    }

    const handleCancel = () => {
        navigate(
            resolveHomeRouteForContext({
                primaryAccount: primaryAccount ?? null,
                activeAccountId,
            }),
        )
    }

    if (isAdviserCreateFlow) {
        return <AddAccountModal open={true} onClose={() => navigate(APP_ROUTES.CLIENTS)} />
    }

    return (
        <Dialog open>
            <DialogContent
                className="m-0 h-full w-full rounded-none grid-rows-[auto_1fr] grid gap-6 top-0 translate-y-0 pb-24 sm:pb-0 p-6 lg:p-6 min-w-0"
                onOpenAutoFocus={(event) => {
                    if (!nextButtonRef.current || nextButtonRef.current.disabled) return
                    event.preventDefault()
                    nextButtonRef.current.focus()
                }}
            >
                <DialogHeader>
                    <div className="self-stretch w-full max-w-[600px] mx-auto flex justify-between items-center">
                        <DialogTitle className="sr-only">{APPLICATION_COPY.create.title}</DialogTitle>
                        <Logo width={128} height={40} />
                        <Button
                            variant="ghost"
                            onClick={handleCancel}
                            className="h-14 py-2.5 bg-white/0 rounded-[100px] flex justify-center items-center gap-2.5 border-0"
                        >
                            <span className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                                Cancel
                            </span>
                        </Button>
                    </div>
                </DialogHeader>

                <div className="w-full max-w-[600px] mx-auto flex flex-col min-h-0">
                    <div className="w-full flex-1 overflow-auto flex flex-col justify-start items-start gap-12">
                        <div className="flex w-full max-w-[600px] flex-col justify-center items-center gap-12">
                            <ApplicationProgressBar active={1} />
                            <div className="w-full flex flex-col justify-start items-start gap-6">
                                <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
                                    <H2 className="justify-start mb-0">{APPLICATION_COPY.create.title}</H2>
                                </div>

                                <AccountTypeSelector
                                    selectedAccountType={selectedAccountType ?? null}
                                    setSelectedAccountType={(t) => setSelectedAccountType(t ?? undefined)}
                                    isTrust={account?.accountType !== AccountType.INDIVIDUAL}
                                    setIsTrust={() => {}}
                                    supportedAccountTypes={[account?.accountType || AccountType.INDIVIDUAL]}
                                />

                                {selectedAccountType && (
                                    <>
                                        <div className="self-stretch justify-start text-sm font-normal leading-tight">
                                            {APPLICATION_COPY.create.description}
                                        </div>
                                        <KycRequirements
                                            frankieOneOnboarding={false}
                                            accountType={selectedAccountType}
                                            title={APPLICATION_COPY.create.requirementTitle}
                                            subtitle={APPLICATION_COPY.create.requirementSubtitle.replace(
                                                '{accountType}',
                                                ACCOUNT_TYPE_LABELS[selectedAccountType].toLowerCase(),
                                            )}
                                            textClassName="text-sm"
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="max-w-[600px] w-full flex justify-end items-end mt-4">
                        <Button
                            ref={nextButtonRef}
                            variant="secondary"
                            className="h-10 px-6 py-2.5 border-0"
                            onClick={goToKycConfirm}
                            disabled={!selectedAccountType || !activeAccountId}
                        >
                            {ONBOARDING_COPY.next}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
